import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table'
import type {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  SortingState,
  VisibilityState,
} from '@tanstack/vue-table'
import { computed, ref, watch } from 'vue'
import type { ViewActiveMapped } from './useTableLibrary'
import type { Elemental } from '~~/src/types/elemental'
import type { ColumnList } from '~~/src/types/library'
import { valueUpdater } from '~~/src/utils'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'

export function useLibraryTableInstance(
  currentData: Ref<Elemental[] | ColumnList[] | ViewActiveMapped>,
  columnsDefinition: Ref<ColumnDef<ColumnList>[]>,
  separatedByType: Ref<boolean>,
) {
  const sorting = ref<SortingState>()
  const columnFilters = ref<ColumnFiltersState>([])
  const columnVisibility = ref<VisibilityState>({})
  const rowSelection = ref({})
  const expanded = ref<ExpandedState>({})
  const { state } = useElementalListsStore()

  const updateColumnFilters = (filters: any) => {
    return valueUpdater(filters, columnFilters)
  }

  const table = useVueTable<ColumnList>({
    data: currentData as Ref<ColumnList[]>,
    columns: columnsDefinition.value,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: updaterOrValue => valueUpdater(updaterOrValue, sorting),
    onColumnFiltersChange: updaterOrValue => updateColumnFilters(updaterOrValue),
    onColumnVisibilityChange: updaterOrValue => valueUpdater(updaterOrValue, columnVisibility),
    onRowSelectionChange: updaterOrValue => valueUpdater(updaterOrValue, rowSelection),
    onExpandedChange: updaterOrValue => valueUpdater(updaterOrValue, expanded),
    initialState: {
      sorting: [
        { id: 'updatedAt', desc: true },
        { id: 'createdAt', desc: true },
      ],
    },
    state: {
      get sorting() {
        return sorting.value
      },
      get columnFilters() {
        return columnFilters.value
      },
      get columnVisibility() {
        return columnVisibility.value
      },
      get rowSelection() {
        return rowSelection.value
      },
      get expanded() {
        return expanded.value
      },
    },
  })

  const headerGroups = computed(() => table.getHeaderGroups())
  const columns = computed(() => {
    const cols = headerGroups.value.flatMap(group => group.headers.map(header => header.column.id))
    return cols
  })

  watch(() => state.viewsActives, () => {
    const filters = Object.values(state.viewsActives || {}).filter(view => view.filterActive)
    table.setColumnFilters([
      {
        id: 'elementalTypeId',
        value: filters.map(f => f.elementaltype),
      },
    ])
  }, { deep: true })

  const updatePageSize = () => {
    if (state.query.length > 0) {
      table.setPageSize(currentData.value.length)
      return
    }

    table.setPageSize(currentData.value.length)
  }

  watch(() => [state.query, currentData.value] as const, () => {
    updatePageSize()
  })

  const handleSeparatedByType = () => {
    table.setColumnFilters([])
    const col = table.getColumn('elementalTypeId')

    if (separatedByType.value)
      col?.toggleVisibility(false)
    else
      col?.toggleVisibility(true)
    table.reset()
    updatePageSize()
  }

  watch(separatedByType, handleSeparatedByType)

  return {
    table,
    headerGroups,
    columns,
    updatePageSize,
  }
}
